/**
 * PasswordInput.jsx
 *
 * Password field with a built-in eye toggle to show/hide the value.
 * Drop-in replacement for the shadcn <Input type="password"> usage.
 *
 * Usage:
 *   <PasswordInput
 *     id="password"
 *     value={password}
 *     onChange={(e) => setPassword(e.target.value)}
 *     style={{ ... }}
 *   />
 */
import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Eye, EyeOff } from 'lucide-react';
export function PasswordInput({
  id = 'password',
  value,
  onChange,
  required = true,
  className = '',
  style,
  testId = 'password-input',
  toggleTestId = 'password-toggle',
  placeholder,
  autoComplete = 'current-password',
  iconColor,
}) {
  const [show, setShow] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        data-testid={testId}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        // pr-10 leaves room for the toggle button on the right.
        className={`pr-10 ${className}`}
        style={style}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={toggleTestId}
        aria-label={show ? 'Hide password' : 'Show password'}
        // Use inset-y-0 + flex centering (not translate-y) so the global
        // `button:hover { transform: translateY(-2px) }` in App.css can't
        // clobber vertical alignment on hover.
        className="absolute right-1 inset-y-0 my-auto h-8 w-8 flex items-center justify-center rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
        // Inline `transform: none` beats the global :hover transform.
        style={{ color: iconColor || '#73766A', transform: 'none' }}
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}
